import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ModeProvider } from "@/context/ModeContext";
import { RoomProvider } from "@/context/RoomContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import SeasonalTint from "@/components/SeasonalTint";
import ZoomManager from "@/components/ZoomManager";

import Landing from "@/pages/Landing";
import AudienceLanding from "@/pages/AudienceLanding";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import JoinClass from "@/pages/JoinClass";
import ClassShowcase from "@/pages/ClassShowcase";
import Dashboard from "@/pages/Dashboard";
import CreateGame from "@/pages/CreateGame";
import GameBuilder from "@/pages/GameBuilder";
import PublishHub from "@/pages/PublishHub";
import GameProject from "@/pages/GameProject";
import GrowthAssistant from "@/pages/GrowthAssistant";
import MyGames from "@/pages/MyGames";
import Discover from "@/pages/Discover";
import ChallengeHistory from "@/pages/ChallengeHistory";
import CommunityHub from "@/pages/CommunityHub";
import ClipsFeed from "@/pages/ClipsFeed";
import PlayGames from "@/pages/PlayGames";
import Favorites from "@/pages/Favorites";
import Notifications from "@/pages/Notifications";
import Activity from "@/pages/Activity";
import Messages from "@/pages/Messages";
import GamePage from "@/pages/GamePage";
import ShareLanding from "@/pages/ShareLanding";
import ReleasePage from "@/pages/ReleasePage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import CreatorLanding from "@/pages/CreatorLanding";
import VerifyCertificate from "@/pages/VerifyCertificate";
import AssetStudio from "@/pages/AssetStudio";
import Templates from "@/pages/Templates";
import CommunityTemplates from "@/pages/CommunityTemplates";
import Achievements from "@/pages/Achievements";
import Arena from "@/pages/Arena";
import CreatorHub from "@/pages/CreatorHub";
import ParentDashboard from "@/pages/ParentDashboard";
import SchoolDashboard from "@/pages/SchoolDashboard";
import Marketplace from "@/pages/Marketplace";
import Rights from "@/pages/Rights";
import Multiplayer from "@/pages/Multiplayer";
import CoopPlay from "@/pages/CoopPlay";
import Earnings from "@/pages/Earnings";
import LevelStudio from "@/pages/LevelStudio";
import MapStudio from "@/pages/MapStudio";
import AIStudio from "@/pages/AIStudio";
import CreatorDashboard from "@/pages/CreatorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import SuperAdmin from "@/pages/SuperAdmin";
import CharacterStudio from "@/pages/CharacterStudio";
import Studio3D from "@/pages/Studio3D";
import AssetHub from "@/pages/AssetHub";
import AssetUniverse from "@/pages/AssetUniverse";
import LivingWorld from "@/pages/LivingWorld";
import CinematicStudio from "@/pages/CinematicStudio";
import CinematicView from "@/pages/CinematicView";
import StoreView from "@/pages/StoreView";
import EmbedStore from "@/pages/EmbedStore";
import WeatherStudio from "@/pages/WeatherStudio";
import MaterialStudio from "@/pages/MaterialStudio";
import GTMStudio from "@/pages/GTMStudio";
import QAStudio from "@/pages/QAStudio";
import GameIRInspector from "@/pages/GameIRInspector";
import UniversalImport from "@/pages/UniversalImport";
import CodeBrain from "@/pages/CodeBrain";
import GameDirector from "@/pages/GameDirector";
import VisualDirector from "@/pages/VisualDirector";
import GameFeelStudio from "@/pages/GameFeelStudio";
import AutopilotStudio from "@/pages/AutopilotStudio";
import GameCompiler from "@/pages/GameCompiler";
import StudioPro from "@/pages/StudioPro";
import StudioProProject from "@/pages/StudioProProject";
import SharedPlan from "@/pages/SharedPlan";
import VibeShare from "@/pages/VibeShare";
import VibeGallery from "@/pages/VibeGallery";
import VibeCollection from "@/pages/VibeCollection";
import VNStudio from "@/pages/VNStudio";
import VNScene from "@/pages/VNScene";
import VNGallery from "@/pages/VNGallery";
import VNPlay from "@/pages/VNPlay";
import MashupStudio from "@/pages/MashupStudio";
import MashupGallery from "@/pages/MashupGallery";
import PlanGallery from "@/pages/PlanGallery";
import Credits from "@/pages/Credits";
import Support from "@/pages/Support";
import Friends from "@/pages/Friends";
import Guilds from "@/pages/Guilds";
import CreatorProfile from "@/pages/CreatorProfile";
import CreatorRace from "@/pages/CreatorRace";
import BuildChallenge from "@/pages/BuildChallenge";
import Community from "@/pages/Community";
import Bridge from "@/pages/Bridge";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";

function Loader() {
  return (
    <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]">
      <div className="w-12 h-12 rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--primary))] animate-spin" />
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role) && user.role !== "superadmin") return <Navigate to="/app" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Loader />;
  if (user) {
    const dest = location.state?.from;
    const to = dest ? `${dest.pathname}${dest.search || ""}` : "/app";
    return <Navigate to={to} replace />;
  }
  return children;
}

function DashboardRedirect() {
  const location = useLocation();
  return <Navigate to={`/app${location.search || ""}`} replace />;
}


function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ModeProvider>
            <RoomProvider>
            <FavoritesProvider>
            <ZoomManager />
            <SeasonalTint />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/for/:audience" element={<AudienceLanding />} />
              <Route path="/challenge" element={<BuildChallenge />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
              <Route path="/join" element={<JoinClass />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/showcase/:code" element={<ClassShowcase />} />
              <Route path="/g/:id" element={<ShareLanding />} />
              <Route path="/release/:id" element={<ReleasePage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/cinematic/:slug" element={<CinematicView />} />
              <Route path="/store/:slug" element={<StoreView />} />
              <Route path="/embed/store/:slug" element={<EmbedStore />} />
              <Route path="/c/:id" element={<CreatorLanding />} />
              <Route path="/vn/:code" element={<VNPlay />} />
              <Route path="/verify" element={<VerifyCertificate />} />
              <Route path="/verify/:certId" element={<VerifyCertificate />} />
              <Route path="/app" element={<Protected><Dashboard /></Protected>} />
              <Route path="/app/create" element={<Protected><CreateGame /></Protected>} />
              <Route path="/app/build/:id" element={<Protected><GameBuilder /></Protected>} />
              <Route path="/app/publish/:id" element={<Protected><PublishHub /></Protected>} />
              <Route path="/app/project/:id" element={<Protected><GameProject /></Protected>} />
              <Route path="/app/levels/:gameId" element={<Protected><LevelStudio /></Protected>} />
              <Route path="/app/map/:gameId" element={<Protected><MapStudio /></Protected>} />
              <Route path="/app/studio/:gameId" element={<Protected><AIStudio /></Protected>} />
              <Route path="/app/living-world/:gameId" element={<Protected><LivingWorld /></Protected>} />
              <Route path="/app/cinematic/:gameId" element={<Protected><CinematicStudio /></Protected>} />
              <Route path="/app/weather/:gameId" element={<Protected><WeatherStudio /></Protected>} />
              <Route path="/app/materials/:gameId" element={<Protected><MaterialStudio /></Protected>} />
              <Route path="/app/gtm/:gameId" element={<Protected><GTMStudio /></Protected>} />
              <Route path="/app/qa/:gameId" element={<Protected><QAStudio /></Protected>} />
              <Route path="/app/ir/:gameId" element={<Protected><GameIRInspector /></Protected>} />
              <Route path="/app/import" element={<Protected><UniversalImport /></Protected>} />
              <Route path="/app/brain/:gameId" element={<Protected><CodeBrain /></Protected>} />
              <Route path="/app/director/:gameId" element={<Protected><GameDirector /></Protected>} />
              <Route path="/app/visual/:gameId" element={<Protected><VisualDirector /></Protected>} />
              <Route path="/app/feel/:gameId" element={<Protected><GameFeelStudio /></Protected>} />
              <Route path="/app/autopilot/:gameId" element={<Protected><AutopilotStudio /></Protected>} />
              <Route path="/app/compiler/:gameId" element={<Protected><GameCompiler /></Protected>} />
              <Route path="/app/studio-pro" element={<Protected roles={["teen", "creator", "educator", "admin"]}><StudioPro /></Protected>} />
              <Route path="/app/studio-pro/:projectId" element={<Protected roles={["teen", "creator", "educator", "admin"]}><StudioProProject /></Protected>} />
              <Route path="/app/plan/:code" element={<Protected><SharedPlan /></Protected>} />
              <Route path="/app/vibe/:code" element={<Protected><VibeShare /></Protected>} />
              <Route path="/app/vibes" element={<Protected><VibeGallery /></Protected>} />
              <Route path="/app/collection/:id" element={<Protected><VibeCollection /></Protected>} />
              <Route path="/app/vn" element={<Protected><VNStudio /></Protected>} />
              <Route path="/app/vn-gallery" element={<Protected><VNGallery /></Protected>} />
              <Route path="/app/vn/:id" element={<Protected><VNScene /></Protected>} />
              <Route path="/app/mashup" element={<Protected roles={["kid", "teen", "student", "creator", "educator", "admin"]}><MashupStudio /></Protected>} />
              <Route path="/app/mashup-gallery" element={<Protected roles={["kid", "teen", "student", "creator", "educator", "admin"]}><MashupGallery /></Protected>} />
              <Route path="/app/gallery" element={<Protected><PlanGallery /></Protected>} />
              <Route path="/app/bridge/:id" element={<Protected><Bridge /></Protected>} />
              <Route path="/app/games" element={<Protected><MyGames /></Protected>} />
              <Route path="/app/arcade" element={<Navigate to="/app/play" replace />} />
              <Route path="/app/hub" element={<Protected><CommunityHub /></Protected>} />
              <Route path="/app/clips" element={<Protected><ClipsFeed /></Protected>} />
              <Route path="/app/play" element={<Protected><PlayGames /></Protected>} />
              <Route path="/app/favorites" element={<Protected><Favorites /></Protected>} />
              <Route path="/app/notifications" element={<Protected><Notifications /></Protected>} />
              <Route path="/app/activity" element={<Protected><Activity /></Protected>} />
              <Route path="/app/messages" element={<Protected><Messages /></Protected>} />
              <Route path="/app/discover" element={<Protected><Discover /></Protected>} />
              <Route path="/app/creator-race" element={<Protected><CreatorRace /></Protected>} />
              <Route path="/app/challenge" element={<Protected><BuildChallenge /></Protected>} />
              <Route path="/app/challenges" element={<Protected><ChallengeHistory /></Protected>} />
              <Route path="/app/arcade/:id" element={<Protected><GamePage /></Protected>} />
              <Route path="/app/assets" element={<Protected><AssetStudio /></Protected>} />
              <Route path="/app/characters" element={<Protected roles={["kid", "teen", "student", "creator", "educator", "admin"]}><CharacterStudio /></Protected>} />
              <Route path="/app/3d" element={<Protected roles={["teen", "student", "creator", "educator", "admin"]}><Studio3D /></Protected>} />
              <Route path="/app/asset-hub" element={<Protected roles={["teen", "student", "creator", "educator", "admin"]}><AssetHub /></Protected>} />
              <Route path="/app/universe" element={<Protected><AssetUniverse /></Protected>} />
              <Route path="/app/credits" element={<Protected><Credits /></Protected>} />
              <Route path="/app/pricing" element={<Protected><Pricing /></Protected>} />
              <Route path="/app/support" element={<Protected><Support /></Protected>} />
              <Route path="/app/friends" element={<Protected><Friends /></Protected>} />
              <Route path="/app/guilds" element={<Protected><Guilds /></Protected>} />
              <Route path="/app/creators/:id" element={<Protected><CreatorProfile /></Protected>} />
              <Route path="/app/community/:gameId" element={<Protected><Community /></Protected>} />
              <Route path="/app/templates" element={<Protected><Templates /></Protected>} />
              <Route path="/app/community-templates" element={<Protected><CommunityTemplates /></Protected>} />
              <Route path="/app/achievements" element={<Protected><Achievements /></Protected>} />
              <Route path="/app/arena" element={<Protected><Arena /></Protected>} />
              <Route path="/app/creator" element={<Protected roles={["creator", "admin"]}><CreatorHub /></Protected>} />
              <Route path="/app/creator-dashboard" element={<Protected roles={["creator", "educator", "admin"]}><CreatorDashboard /></Protected>} />
              <Route path="/app/parent" element={<Protected roles={["parent", "admin"]}><ParentDashboard /></Protected>} />
              <Route path="/app/school" element={<Protected roles={["educator", "admin"]}><SchoolDashboard /></Protected>} />
              <Route path="/app/marketplace" element={<Protected roles={["teen", "creator", "player", "educator", "admin"]}><Marketplace /></Protected>} />
              <Route path="/app/rights" element={<Protected roles={["teen", "creator", "educator", "admin"]}><Rights /></Protected>} />
              <Route path="/app/multiplayer" element={<Protected roles={["teen", "creator", "player", "educator", "admin"]}><Multiplayer /></Protected>} />
              <Route path="/app/coop/:roomId" element={<Protected roles={["teen", "creator", "player", "educator", "admin"]}><CoopPlay /></Protected>} />
              <Route path="/app/earnings" element={<Protected roles={["creator", "educator", "admin"]}><Earnings /></Protected>} />
              <Route path="/app/growth" element={<Protected roles={["creator", "educator", "admin"]}><GrowthAssistant /></Protected>} />
              <Route path="/app/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
              <Route path="/app/superadmin" element={<Protected roles={["superadmin"]}><SuperAdmin /></Protected>} />
              <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
              <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
              <Route path="/app/dashboard" element={<DashboardRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-center" richColors />
            </FavoritesProvider>
            </RoomProvider>
          </ModeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
